import Bugsnag from '@bugsnag/js';
import { PricingApiError } from './types';

// Server codes are untrusted strings. Never retain an arbitrary code (or a
// title/message) in metadata, breadcrumbs, or the grouping hash.
const OPERATIONAL_CODES = new Set([
  'invalid_promo',
  'quote_refresh_required',
  'payment_declined',
  'payment_declined_after_cancel',
  'payment_pending_review',
  'payment_needs_review',
  'payment_attempt_released',
  'pricing_unavailable',
  'purchase_path_not_ready',
  'refund_not_ready',
  'unlinked_contract',
  'unresolved_contract',
  'purchase_result_not_ready',
  'purchase_result_unavailable',
  'purchase_response_unavailable',
]);
export type PricingEndpoint =
  | '/api/pricing/config'
  | '/api/pricing/catalog'
  | '/api/pricing/quotes'
  | '/api/pricing/subscription/quotes'
  | '/api/pricing/downgrade/quotes'
  | '/api/pricing/payment_method/quotes'
  | '/api/pricing/promo_preview'
  | '/api/pricing/attempts/:id'
  | '/api/pricing/attempts/:id/result'
  | '/api/pricing/billing_contract'
  | '/api/user/refresh_token'
  | '/api/subscription'
  | 'unknown';

interface FailureContext {
  endpoint: PricingEndpoint;
  failure: 'http' | 'timeout' | 'network' | 'aborted' | 'transport' | 'invalid_contract' | 'authentication' | 'application';
}
const failureContexts = new WeakMap<object, FailureContext>();
const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null;

/** Attach request provenance without reporting, retaining URLs, or changing the thrown error. */
export function withPricingFailureContext<T>(error: T, context: FailureContext): T {
  if (isObject(error)) failureContexts.set(error, context);
  return error;
}

export function copyPricingFailureContext<T>(error: T, source: unknown): T {
  const context = isObject(source) ? failureContexts.get(source) : undefined;
  return context ? withPricingFailureContext(error, context) : error;
}

interface FailureMetadata {
  stage: string;
  endpoint: PricingEndpoint;
  failure: FailureContext['failure'] | 'unknown';
  status?: number;
  code?: string;
  code_fingerprint?: string;
  classification?: 'unavailable';
}

/** Only allowlisted operational fields: never attach source errors, bodies, quotes or tokens. */
export async function reportPricingFailure(stage: string, error?: unknown): Promise<void> {
  // The caller owns reporting. Reads only annotate errors, so caller stage and
  // sanitized endpoint are both retained in the single report.
  const context = isObject(error) ? failureContexts.get(error) : undefined;
  const metadata: FailureMetadata = {
    stage,
    endpoint: context?.endpoint ?? 'unknown',
    failure: context?.failure ?? 'unknown',
  };
  try {
    if (error instanceof PricingApiError) metadata.status = error.status;
    const detail = error instanceof PricingApiError ? error.detail : error;
    const rawCode =
      typeof detail === 'object' && detail !== null && 'code' in detail ? detail.code : undefined;
    if (typeof rawCode === 'string' && rawCode) {
      if (OPERATIONAL_CODES.has(rawCode)) metadata.code = rawCode;
      else {
        // A new code gets its own alert group without disclosing arbitrary text.
        metadata.code = 'unrecognized';
        metadata.code_fingerprint = 'unavailable';
        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(rawCode));
        metadata.code_fingerprint = Array.from(new Uint8Array(digest), (b) =>
          b.toString(16).padStart(2, '0'),
        ).join('');
      }
    }
  } catch {
    metadata.classification = 'unavailable';
  }
  // Explicit routine outcomes only. Auth failures, unknown rejections, and
  // ambiguous payment states remain observable regardless of their HTTP status.
  const expectedOutcome =
    (metadata.code === 'invalid_promo' && metadata.status === 422 &&
      ['quote_selection', 'promo_preview'].includes(stage)) ||
    (metadata.code === 'payment_declined' && metadata.status === undefined && stage === 'api_error');
  if (expectedOutcome) {
    try {
      if (typeof window !== 'undefined' && Bugsnag.isStarted())
        Bugsnag.leaveBreadcrumb('pricing.expected_outcome', { ...metadata });
    } catch {
      /* Optional breadcrumbs must not change payment state. */
    }
    return;
  }
  try {
    if (typeof window !== 'undefined' && Bugsnag.isStarted())
      Bugsnag.notify(new Error('Subscription pricing failure'), (event) => {
        event.severity = 'warning';
        event.groupingHash = [
          'pricing', stage, metadata.endpoint, metadata.failure,
          metadata.status ?? 'unknown', metadata.code ?? 'unknown',
          ...(metadata.code_fingerprint ? [metadata.code_fingerprint] : []),
        ].join(':');
        event.addMetadata('pricing', metadata);
      });
  } catch {
    /* Still log locally when the SDK fails. A later caller may retry reporting. */
  }
  try {
    console.error('pricing.failure', metadata);
  } catch {
    /* Optional reporting must not change payment state. */
  }
}
